import { ReactElement, ReactNode, useMemo } from 'react'
import { useCollaboratorTicketContext } from '../collaborator-ticket-provider'
import { DesignRequestStatus } from 'lib/api/tickets/tickets'
import { TicketStatusBadge } from 'lib/components/badge/badge'
import Button from 'components/core/button'
import Breadcrumb from 'lib/components/breadcrumb/breadcrumb'
import { useCollaboratorContext } from 'providers/collaborator-context-provider'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'

export default function CollaboratorTicketHeader(): ReactElement {
  const { submitFeedback, ticket } = useCollaboratorTicketContext()
  const { allCollaborationRequestsPath } = useCollaboratorContext()
  const { isFeatureFlagEnabled } = useFeatureFlagsContext()
  const wipPreviewActionsEnabled = isFeatureFlagEnabled('wip_preview_actions')
  const viewAllAssetsEnabled = isFeatureFlagEnabled('collaborator_view_all_assets')
  const requestSubject = ticket?.subject || ''
  const isInReview = useMemo(() => ticket?.status === DesignRequestStatus.review, [ticket?.status])
  const isInDraft = useMemo(() => ticket?.status === DesignRequestStatus.draft, [ticket?.status])
  const isInPreview = useMemo(() => ticket?.status === DesignRequestStatus.preview, [ticket?.status])
  const canSubmitFeedback = isInReview || isInDraft || (isInPreview && wipPreviewActionsEnabled)
  const updateCount = useMemo(() => {
    return ticket?.meta.pendingUpdates || 0
  }, [ticket?.meta.pendingUpdates])

  async function handleSubmitFeedback() {
    try {
      await submitFeedback()
    } catch (e) {
      console.error('CollaboratorTicketHeader Error:', e)
    }
  }

  return (
    <header>
      <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-pb-3 tw-pt-4">
        <div className="tw-ml-2 tw-flex tw-flex-col tw-items-start tw-gap-1">
          <div className="tw-flex tw-items-center tw-gap-2">
            <h2 className="tw-m-0 tw-text-2xl">{requestSubject}</h2>
            <TicketStatusBadge status={ticket?.status} className="tw-ml-0" />
          </div>
          {viewAllAssetsEnabled && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Breadcrumb text="All collaboration requests" href={allCollaborationRequestsPath} />
              <span className="">/</span>
              <span className="tw-font-bold tw-text-neutral-600">{requestSubject}</span>
            </div>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-right">
          {canSubmitFeedback && (
            <FeedbackButton count={updateCount} onClick={handleSubmitFeedback}>
              Send Feedback
            </FeedbackButton>
          )}
        </div>
      </div>
    </header>
  )
}

interface FeedbackButtonProps {
  children: ReactNode
  count: number
  onClick: () => void
}

function FeedbackButton({ children, count, onClick }: FeedbackButtonProps): ReactElement {
  const color = count ? 'purple' : 'lightGray'

  return (
    <Button type="button" color={color} disabled={!count} onClick={onClick}>
      {!!count && <span className="tw-mr-2 tw-rounded-full tw-bg-white tw-px-2 tw-text-neutral-800">{count}</span>}
      {children}
    </Button>
  )
}
